import React, { useMemo, useState, useEffect } from 'react';
import { Button, Modal, Form, Input, message, Card, Popconfirm, InputNumber, Checkbox, Typography, Collapse, Select, Tag, Upload, Image, Row, Col, Space, Divider, ColorPicker, Switch } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, LinkOutlined, CloseOutlined, MinusCircleOutlined, ArrowUpOutlined, ArrowDownOutlined, SwapOutlined, CheckOutlined, HolderOutlined, UploadOutlined, InfoCircleOutlined, CopyOutlined } from '@ant-design/icons';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, horizontalListSortingStrategy, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { RootState, AppDispatch } from '../store';
import { fetchPujas, createPuja, updatePuja, deletePuja, updatePujaLine, deletePujaLine, addPujaLines, addPujaBidhiLink, addPujaSankalpaLink, removePujaBidhiLink, removePujaSankalpaLink, updatePujaBidhiLinkOrder, bulkUpdatePujaLineOrder, addPujaToState } from '../store/pujaSlice';
import { Bidhi, CreatePuja, Puja, PujaLine, Sankalpa } from '../types';
import { fetchBidhis } from '../store/bidhiSlice';
import { fetchSankalpas } from '../store/sankalpaSlice';
import { pujaLineLinkApi, pujaStyleTemplatesApi, pujaTagAPI, pujaApi, API_URL } from '../services/api';
import AddPujaModal from '../components/AddPujaModal';
import AddLineModal from '../components/AddLineModal';
import PujaLinkingSelectionModal from '../components/puja/PujaLinkingSelectionModal';
import PujaBidhiLinkModal from '../components/puja/PujaBidhiLinkModal';
import PujaSankalpaLinkModal from '../components/puja/PujaSankalpaLinkModal';
import { DEFAULT_BOOK_STYLES, loadBookDefaultStyles } from '../utils/bookDefaults';
import { BookDefaultStylesEditor } from '../components/BookDefaultStylesEditor';
import { bookDefaultsToFormValues, formValuesToBookDefaults } from '../utils/bookDefaultTemplates';

const PujaPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { pujas, loading } = useSelector((state: RootState) => state.puja);
  const { bidhis } = useSelector((state: RootState) => state.bidhi);
  const { sankalpas } = useSelector((state: RootState) => state.sankalpa);
  
  const [addPujaModalVisible, setAddPujaModalVisible] = useState<boolean>(false);
  const [editPujaModalVisible, setEditPujaModalVisible] = useState<boolean>(false);
  const [editLineModalVisible, setEditLineModalVisible] = useState<boolean>(false);
  const [addLineModalVisible, setAddLineModalVisible] = useState<boolean>(false);
  const [linkingModalVisible, setLinkingModalVisible] = useState<boolean>(false);
  const [bidhiLinkModalVisible, setBidhiLinkModalVisible] = useState<boolean>(false);
  const [sankalpaLinkModalVisible, setSankalpaLinkModalVisible] = useState<boolean>(false);
  const [selectedPujaId, setSelectedPujaId] = useState<number | null>(null);
  const [selectedLineId, setSelectedLineId] = useState<number | null>(null);
  const [reorderMode, setReorderMode] = useState<{ pujaId: number; lineId: number } | null>(null);
  const [reorderedLinks, setReorderedLinks] = useState<Map<string, any[]>>(new Map());
  const [pujaLineReorderMode, setPujaLineReorderMode] = useState<number | null>(null);
  const [reorderedPujaLines, setReorderedPujaLines] = useState<Map<number, PujaLine[]>>(new Map());
  const [expandedPujas, setExpandedPujas] = useState<string[]>([]);
  const [pujaTags, setPujaTags] = useState<any[]>([]);
  const [loadingPujaTags, setLoadingPujaTags] = useState(false);
  const [creatingPujaTag, setCreatingPujaTag] = useState(false);
  const [pujaTagSearchValue, setPujaTagSearchValue] = useState('');
  const [activePujaTagFieldName, setActivePujaTagFieldName] = useState<number | null>(null);
  const [editPujaFileList, setEditPujaFileList] = useState<any[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState<string>(searchParams.get('q') || '');
  const [tagFilterIds, setTagFilterIds] = useState<number[]>(
    searchParams.get('tags') ? searchParams.get('tags')!.split(',').map(Number).filter(n => !isNaN(n)) : []
  );
  const [duplicatingPujaId, setDuplicatingPujaId] = useState<number | null>(null);
  const [bookDefaultsModalOpen, setBookDefaultsModalOpen] = useState(false);
  const [bookDefaultsForm] = Form.useForm();
  const [styleTemplatesLoading, setStyleTemplatesLoading] = useState(false);
  const [styleTemplates, setStyleTemplates] = useState<Array<{ id: number; name: string; styles: any }>>([]);
  const [activeStyleTemplateId, setActiveStyleTemplateId] = useState<number | null>(null);
  const [selectedStyleTemplateId, setSelectedStyleTemplateId] = useState<number | null>(null);
  const [selectedStyleTemplateBase, setSelectedStyleTemplateBase] = useState(() => loadBookDefaultStyles());
  // Template name is stored in `bookDefaultsForm` with validation.
  const navigate = useNavigate();

  // Sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const [editPujaForm] = Form.useForm();
  const [editLineForm] = Form.useForm();
  const [addLineForm] = Form.useForm();

  useEffect(() => {
    dispatch(fetchPujas());
    if (!bidhis || bidhis.length === 0) {
      dispatch(fetchBidhis());
    }
    if (!sankalpas || sankalpas.length === 0) {
      dispatch(fetchSankalpas());
    }
  }, [dispatch]);

  const refreshStyleTemplates = async () => {
    setStyleTemplatesLoading(true);
    try {
      const res = await pujaStyleTemplatesApi.list();
      const templates = Array.isArray(res.data?.templates) ? res.data.templates : [];
      const activeIdFromId = (res.data?.activeTemplateId ?? null) as number | null;
      const activeIdFromFlag =
        templates.find((t: any) => (t as any).isActive === true)?.id != null
          ? Number(templates.find((t: any) => (t as any).isActive === true)!.id)
          : null;
      const activeId = activeIdFromId ?? activeIdFromFlag;
      setStyleTemplates(templates);
      setActiveStyleTemplateId(activeId);

      // One-time migration: if user has legacy local defaults but no backend templates yet.
      try {
        const legacyRaw = localStorage.getItem('bookDefaultStyles:v1');
        const migratedFlag = localStorage.getItem('bookDefaultStyles:importedToBackend:v1');
        if (templates.length === 0 && legacyRaw && !migratedFlag) {
          Modal.confirm({
            title: 'Import existing local default styles?',
            content: 'We found default styles saved locally from earlier. Do you want to import them as your first template?',
            okText: 'Import',
            cancelText: 'Skip',
            onOk: async () => {
              const legacy = loadBookDefaultStyles();
              const created = await pujaStyleTemplatesApi.create({ name: 'Imported defaults', styles: legacy });
              const createdId = created.data?.id;
              if (createdId != null) await pujaStyleTemplatesApi.update(createdId, { isActive: true });
              localStorage.setItem('bookDefaultStyles:importedToBackend:v1', '1');
              message.success('Imported');
              await refreshStyleTemplates();
            },
            onCancel: () => {
              localStorage.setItem('bookDefaultStyles:importedToBackend:v1', 'skipped');
            },
          });
        }
      } catch {
        // ignore migration errors
      }

      return { templates, activeId };
    } finally {
      setStyleTemplatesLoading(false);
    }
  };

  const getDraftStyles = () => {
    const vals = bookDefaultsForm.getFieldsValue();
    return formValuesToBookDefaults(vals, selectedStyleTemplateBase as any);
  };

  const promptForName = async (title: string, initialValue = ''): Promise<string | null> => {
    return await new Promise((resolve) => {
      let name = initialValue;
      Modal.confirm({
        title,
        // Must be above the already-open Book default styles modal.
        zIndex: 20000,
        content: (
          <Input
            autoFocus
            defaultValue={initialValue}
            placeholder="Template name"
            onChange={(e) => (name = e.target.value)}
            onPressEnter={() => {
              const v = name.trim();
              if (!v) return;
              Modal.destroyAll();
              resolve(v);
            }}
          />
        ),
        okText: 'Save',
        cancelText: 'Cancel',
        onOk: () => {
          const v = name.trim();
          resolve(v ? v : null);
        },
        onCancel: () => resolve(null),
      });
    });
  };

  const selectTemplate = (templateId: number | null) => {
    setSelectedStyleTemplateId(templateId);
    const t = templateId != null ? (styleTemplates as any[]).find((x) => Number(x.id) === Number(templateId)) : undefined;
    const base = t?.styles || DEFAULT_BOOK_STYLES;
    setSelectedStyleTemplateBase(base);
    bookDefaultsForm.setFieldsValue(bookDefaultsToFormValues(base));
    bookDefaultsForm.setFieldValue('templateName', typeof t?.name === 'string' ? t.name : '');
    bookDefaultsForm.setFieldValue('saveAsNew', false);
  };

  const handleSaveTemplate = async () => {
    const saveAsNew = !!bookDefaultsForm.getFieldValue('saveAsNew');
    if (saveAsNew) {
      const { templateName } = await bookDefaultsForm.validateFields(['templateName']);
      const name = String(templateName || '').trim();
      await handleCreateTemplate(name, true);
      setBookDefaultsModalOpen(false);
      message.success('Template saved');
      return;
    }
    if (selectedStyleTemplateId == null) {
      message.warning('Select a template, or turn on “Save as new”');
      return;
    }
    const styles = getDraftStyles();
    await pujaStyleTemplatesApi.update(selectedStyleTemplateId, { styles });
    message.success('Template updated');
    await refreshStyleTemplates();
  };

  const handleCreateTemplate = async (name: string, makeActive: boolean) => {
    const styles = getDraftStyles();
    const created = await pujaStyleTemplatesApi.create({ name, styles });
    message.success('Template created');
    const { templates } = await refreshStyleTemplates();
    const createdId = created.data?.id ?? templates.find((t: any) => t.name === name)?.id;
    if (createdId != null) {
      selectTemplate(Number(createdId));
      if (makeActive) {
        await pujaStyleTemplatesApi.update(Number(createdId), { isActive: true });
        setActiveStyleTemplateId(Number(createdId));
        message.success('Active template updated');
      }
    }
  };

  const handleUpdateTemplate = async () => {
    if (selectedStyleTemplateId == null) {
      try {
        const { templateName } = await bookDefaultsForm.validateFields(['templateName']);
        const name = String(templateName || '').trim();
        await handleCreateTemplate(name, true);
        return;
      } catch {
        bookDefaultsForm.scrollToField?.('templateName', { block: 'center' } as any);
        try {
          (bookDefaultsForm as any).getFieldInstance?.('templateName')?.focus?.();
        } catch {
          // ignore
        }
        return;
      }
    }
    const styles = getDraftStyles();
    await pujaStyleTemplatesApi.update(selectedStyleTemplateId, { styles });
    message.success('Template updated');
    await refreshStyleTemplates();
  };

  const handleDeleteTemplate = async () => {
    if (selectedStyleTemplateId == null) return;
    await pujaStyleTemplatesApi.delete(selectedStyleTemplateId);
    message.success('Template deleted');
    const { templates, activeId } = await refreshStyleTemplates();
    const nextActive = activeId != null ? templates.find((t: any) => Number(t.id) === Number(activeId)) : undefined;
    const base = nextActive?.styles || DEFAULT_BOOK_STYLES;
    setSelectedStyleTemplateId(nextActive?.id ?? null);
    setSelectedStyleTemplateBase(base);
    bookDefaultsForm.setFieldsValue(bookDefaultsToFormValues(base));
  };

  const handleSetActive = async (templateId: number | null) => {
    if (templateId == null) {
      message.warning('Backend does not support clearing active template via API.');
      return;
    }
    await pujaStyleTemplatesApi.update(templateId, { isActive: true });
    setActiveStyleTemplateId(templateId);
    message.success('Active template updated');
  };

  const handleAddLines = async (values: { lines: { content: string; isVisible?: boolean; metadata?: { key: string; value: string }[] }[] }) => {
    try {
      const currentPuja = pujas.find(p => p.id === selectedPujaId);
      const maxOrder = currentPuja?.lines?.length ? Math.max(...currentPuja.lines.map(line => line.order || 0)) : 0;
      
      const linesWithOrder = values.lines.map((line, index) => {
        const metadata: Record<string, string> = {};
        if (line.metadata) {
          line.metadata.forEach(item => {
            if (item.key && item.value) {
              metadata[item.key] = item.value;
            }
          });
        }
        return {
          content: line.content,
          isVisible: line.isVisible ?? true,
          order: maxOrder + index + 1,
          pujaId: selectedPujaId!,
          ...(Object.keys(metadata).length ? { metadata } : {}),
        };
      });
      
      await dispatch(addPujaLines({ pujaId: selectedPujaId!, lines: linesWithOrder })).unwrap();
      message.success('Lines added successfully');
      setAddLineModalVisible(false);
      addLineForm.resetFields();
    } catch (error) {
      console.error('Error adding lines:', error);
      message.error('Failed to add lines');
    }
  };

  const handleAddPuja = async (values: (CreatePuja & { tagIds?: number[] }) | { formData: FormData; tagIds?: number[] }) => {
    try {
      let response: Puja;
      
      if ('formData' in values) {
        // Handle FormData (with profile picture)
        const { formData, tagIds } = values;
        const apiResponse = await pujaApi.create(formData);
        response = apiResponse.data;
        
        // Associate tags with puja
        if (tagIds && tagIds.length > 0 && response.id) {
          try {
            await Promise.all(
              tagIds.map(tagId => pujaTagAPI.associateTagWithPuja(tagId, response.id!))
            );
          } catch (tagError) {
            console.error('Error associating tags:', tagError);
            message.warning('Puja created but some tags may not have been associated');
          }
        }
      } else {
        // Handle regular JSON
        const { tagIds, ...pujaData } = values;
        response = await dispatch(createPuja(pujaData)).unwrap();
        
        // Associate tags with puja
        if (tagIds && tagIds.length > 0 && response.id) {
          try {
            await Promise.all(
              tagIds.map(tagId => pujaTagAPI.associateTagWithPuja(tagId, response.id!))
            );
          } catch (tagError) {
            console.error('Error associating tags:', tagError);
            message.warning('Puja created but some tags may not have been associated');
          }
        }
      }
      
      
      // Fetch complete puja data with tags and add to state
      if (response.id) {
        try {
          const completePujaResponse = await pujaApi.getById(response.id);
          dispatch(addPujaToState(completePujaResponse.data));
        } catch (fetchError) {
          console.error('Error fetching complete puja:', fetchError);
          // Fallback: add the response we have
          dispatch(addPujaToState(response));
        }
      } else {
        // If no ID, just add what we have
        dispatch(addPujaToState(response));
      }
      
      message.success('Puja created successfully');
      setAddPujaModalVisible(false);
    } catch (error) {
      console.error('Error creating puja:', error);
      message.error('Failed to create puja');
    }
  };

  const handleEditPuja = async (values: any) => {
    try {
      // Process metadata similar to GranthaPage
      const metadata: any = {
        bidha: values.bidha || '',
        upabidha: values.upabidha || '',
        prakar: values.prakar || '',
        upaprakar: values.upaprakar || '',
        aupaprakar: values.aupaprakar || '',
      };
      
      if (values.customMetadata) {
        values.customMetadata.forEach((item: { key: string; value: string }) => {
          if (item.key && item.value) {
            metadata[item.key] = item.value;
          }
        });
      }
      
      const hasProfilePicture = editPujaFileList.length > 0 && editPujaFileList[0].originFileObj;
      
      if (hasProfilePicture) {
        // Use FormData for file upload
        const formData = new FormData();
        formData.append('name', values.name);
        formData.append('metadata', JSON.stringify(metadata));
        formData.append('profilePicture', editPujaFileList[0].originFileObj);
        
        await pujaApi.update(selectedPujaId!, formData);
      } else {
      await dispatch(updatePuja({ 
        id: selectedPujaId!, 
        data: { 
          name: values.name,
          metadata: metadata
        } 
      })).unwrap();
      }
      
      // Update tags
      const currentPuja = pujas.find(p => p.id === selectedPujaId);
      const currentTagIds = currentPuja?.tags?.map(tag => tag.id) || [];
      const newTagIds = values.tagIds?.filter((id: any) => id !== undefined) || [];
      
      // Find tags to remove (in current but not in new)
      const tagsToRemove = currentTagIds.filter(id => !newTagIds.includes(id));
      
      // Find tags to add (in new but not in current)
      const tagsToAdd = newTagIds.filter((id: number) => !currentTagIds.includes(id));
      
      // Remove tags
      for (const tagId of tagsToRemove) {
        try {
          await pujaTagAPI.removeTagFromPuja(tagId, selectedPujaId!);
        } catch (error) {
          console.error('Error removing tag:', error);
        }
      }
      
      // Add tags
      for (const tagId of tagsToAdd) {
        try {
          await pujaTagAPI.associateTagWithPuja(tagId, selectedPujaId!);
        } catch (error) {
          console.error('Error adding tag:', error);
        }
      }
      
      message.success('Puja updated successfully');
      setEditPujaModalVisible(false);
      editPujaForm.resetFields();
    } catch (error) {
      console.error('Error updating puja:', error);
      message.error('Failed to update puja');
    }
  };

  const handleEditLine = async (values: { content: string; order?: number; isVisible?: boolean; metadata?: { key: string; value: string }[] }) => {
    try {
      const metadata: Record<string, string> = {};
      if (values.metadata) {
        values.metadata.forEach(item => {
          if (item.key && item.value) {
            metadata[item.key] = item.value;
          }
        });
      }

      await dispatch(
        updatePujaLine({
          lineId: selectedLineId!,
          data: {
            content: values.content,
            order: values.order,
            isVisible: values.isVisible,
            ...(Object.keys(metadata).length ? { metadata } : {}),
          },
        }),
      ).unwrap();
      message.success('Line updated successfully');
      setEditLineModalVisible(false);
      editLineForm.resetFields();
    } catch (error) {
      console.error('Error updating line:', error);
      message.error('Failed to update line');
    }
  };

  const handleDeleteLine = async (lineId: number) => {
    try {
      await dispatch(deletePujaLine(lineId)).unwrap();
      message.success('Line deleted successfully');
    } catch (error) {
      console.error('Error deleting line:', error);
      message.error('Failed to delete line');
    }
  };

  const handleDeletePuja = async (id: number) => {
    try {
      await dispatch(deletePuja(id)).unwrap();
      message.success('Puja deleted successfully');
    } catch (error) {
      console.error('Error deleting puja:', error);
      message.error('Failed to delete puja');
    }
  };

  const handleDuplicatePuja = async (puja: Puja) => {
    const sourceId = Number(puja?.id);
    if (!sourceId) return;

    try {
      setDuplicatingPujaId(sourceId);

      // Ensure we have the full Puja: lines + links
      const sourcePuja = (await pujaApi.getById(sourceId)).data;

      // 1) Copy name and add " copy"
      // 2) Copy profile picture reference (best-effort: backend may accept this string)
      // 3) Copy metadata
      // 4) Copy lines (then recreate links via network calls)
      const createPayload: any = {
        name: `${sourcePuja.name} copy`,
        ...(sourcePuja.metadata ? { metadata: { ...sourcePuja.metadata } } : {}),
        ...(sourcePuja.profilePicture ? { profilePicture: sourcePuja.profilePicture } : {}),
      };

      const created = (await pujaApi.create(createPayload)).data;
      if (!created?.id) throw new Error('Duplicate puja create did not return id');

      const sourceLines = [...(sourcePuja.lines || [])]
        .sort((a: any, b: any) => (a.order || 0) - (b.order || 0));

      const linesToCreate = sourceLines.map((l: any) => ({
        content: l.content,
        order: l.order,
        isVisible: l.isVisible ?? true,
        ...(l.metadata && typeof l.metadata === 'object' && !Array.isArray(l.metadata) ? { metadata: l.metadata } : {}),
      }));

      let createdLines: any[] = [];
      if (linesToCreate.length > 0) {
        const addLinesResp = await pujaApi.addLines(created.id, linesToCreate);
        createdLines = (addLinesResp.data?.lines || addLinesResp.data || []) as any[];
      }

      const targetLines = [...createdLines].sort((a: any, b: any) => (a.order || 0) - (b.order || 0));

      // Recreate links per line (network requests)
      for (let i = 0; i < sourceLines.length; i++) {
        const srcLine: any = sourceLines[i];
        const dstLine: any = targetLines[i];
        if (!dstLine?.id) continue;

        const bidhiLinks = [...(srcLine.bidhiLinks || [])].sort((a: any, b: any) => (a.order || 0) - (b.order || 0));
        for (const link of bidhiLinks) {
          await pujaLineLinkApi.createBidhiLink({
            pujaLineId: dstLine.id,
            bidhiId: link.bidhiId,
            position: link.position || 0,
            order: link.order || 0,
            entireBidhi: !!link.entireBidhi,
            selectedBidhiLineIds: link.selectedBidhiLineIds || [],
          });
        }

        const sankalpaLinks = [...(srcLine.sankalpaLinks || [])];
        for (const link of sankalpaLinks) {
          await pujaLineLinkApi.createSankalpaLink({
            pujaLineId: dstLine.id,
            sankalpaId: link.sankalpaId,
            position: link.position || 0,
            selectedSankalpaLineIds: link.selectedSankalpaLineIds || [],
          });
        }
      }

      // Refresh state with the full duplicated puja
      const completeNew = (await pujaApi.getById(created.id)).data;
      dispatch(addPujaToState(completeNew));

      message.success('Puja duplicated successfully');
      navigate(`/pujaEdit/${created.id}`);
    } catch (error) {
      console.error('Error duplicating puja:', error);
      message.error('Failed to duplicate puja');
    } finally {
      setDuplicatingPujaId(null);
    }
  };

  const findCurrentLine = (): { puja: any; line: PujaLine } | null => {
    if (!selectedLineId) return null;
    for (const puja of pujas) {
      const line = puja.lines?.find((l: PujaLine) => l.id === selectedLineId);
      if (line) {
        return { puja, line };
      }
    }
    return null;
  };

  const handleBidhiLinkConfirm = async (
    selections: Array<{ bidhiId: number; order: number; entireBidhi: boolean; selectedLineIds: number[] }>,
    startingPosition?: number | null,
    titleVisible: boolean = true,
    linkAsShortcut?: boolean,
    displayName?: string,
  ) => {
    console.log('Puja-Bidhi link confirm payload:', {
      selections,
      startingPosition,
      selectedLineId,
    });
    try {
      const result = findCurrentLine();
      if (!result) {
        message.error('Unable to find selected line to link');
        return;
      }

      const { line } = result;
      const position = startingPosition ?? 0;
      
      // Find existing links at the same position to continue order
      const existingLinksAtPosition = (line.bidhiLinks || []).filter(
        (link) => link.position === position
      );
      const maxOrder = existingLinksAtPosition.length > 0
        ? Math.max(...existingLinksAtPosition.map(link => link.order ?? -1))
        : -1;
      const baseOrder = maxOrder + 1;
      
      // Make one API call for each selected bidhi
      const promises = selections.map(async (selection) => {
        const response = await pujaLineLinkApi.createBidhiLink({
          pujaLineId: line.id!,
          bidhiId: selection.bidhiId,
          position,
          order: baseOrder + selection.order, // Continue from existing max order
          entireBidhi: selection.entireBidhi,
          selectedBidhiLineIds: selection.entireBidhi ? [] : selection.selectedLineIds,
          titleVisible,
          shortcut: linkAsShortcut,
          displayName: linkAsShortcut ? displayName : undefined,
        });
        const createdLink = response.data as any;
        const bidhi = (bidhis as Bidhi[]).find(b => b.id === selection.bidhiId);
        dispatch(
          addPujaBidhiLink({
            pujaLineId: line.id!,
            link: {
              ...createdLink,
              titleVisible: createdLink.titleVisible ?? titleVisible,
              shorcut: createdLink.shorcut ?? linkAsShortcut,
              displayName: createdLink.displayName ?? displayName,
              bidhi: bidhi ? { id: bidhi.id, name: bidhi.name } : { id: selection.bidhiId, name: '' },
            },
          }),
        );
        return { bidhiId: selection.bidhiId, success: true };
      });

      await Promise.all(promises);
      message.success(`Linked puja line to ${selections.length} bidhi(s) successfully`);
      setBidhiLinkModalVisible(false);
    } catch (error) {
      console.error('Error linking puja line to bidhis:', error);
      message.error('Failed to link puja line to bidhis');
    }
  };

  const handleSankalpaLinkConfirm = async (
    sankalpaId: number,
    sankalpaLineIds?: number[],
    startingPosition?: number | null,
  ) => {
    console.log('Puja-Sankalpa link confirm payload:', {
      sankalpaId,
      sankalpaLineIds,
      startingPosition,
      selectedLineId,
    });
    try {
      const result = findCurrentLine();
      if (!result) {
        message.error('Unable to find selected line to link');
        return;
      }

      const { line } = result;
      // Call backend to create Sankalpa link for this Puja line
      const response = await pujaLineLinkApi.createSankalpaLink({
        pujaLineId: line.id!,
        sankalpaId,
        position: startingPosition ?? 0,
        selectedSankalpaLineIds: sankalpaLineIds && sankalpaLineIds.length ? sankalpaLineIds : [],
      });
      const createdLink = response.data as any;
      const sankalpa = (sankalpas as Sankalpa[]).find(s => s.id === sankalpaId);
      dispatch(
        addPujaSankalpaLink({
          pujaLineId: line.id!,
          link: {
            ...createdLink,
            sankalpa: sankalpa ? { id: sankalpa.id, name: sankalpa.name } : { id: sankalpaId, name: '' },
          },
        }),
      );

      message.success('Linked puja line to sankalpa successfully');
      setSankalpaLinkModalVisible(false);
    } catch (error) {
      console.error('Error linking puja line to sankalpa:', error);
      message.error('Failed to link puja line to sankalpa');
    }
  };

  const handleDeleteBidhiLink = async (linkId: number) => {
    try {
      await pujaLineLinkApi.deleteBidhiLink(linkId);
      dispatch(removePujaBidhiLink({ linkId }));
      message.success('Bidhi link detached successfully');
    } catch (error) {
      console.error('Error deleting bidhi link:', error);
      message.error('Failed to detach bidhi link');
    }
  };

  const handleBidhiLinkDragEnd = (event: any, pujaId: number, lineId: number, position: number) => {
    const { active, over } = event;
    if (active.id !== over?.id && over?.id) {
      const key = `${pujaId}-${lineId}-${position}`;
      const links = [...(reorderedLinks.get(key) || [])];
      const oldIndex = links.findIndex(link => `bidhi-link-${link.id}` === active.id);
      const newIndex = links.findIndex(link => `bidhi-link-${link.id}` === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const newLinks = arrayMove(links, oldIndex, newIndex);
        setReorderedLinks(new Map(reorderedLinks.set(key, newLinks)));
      }
    }
  };

  const handlePujaLineDragEnd = (event: any, pujaId: number) => {
    const { active, over } = event;
    if (active.id !== over?.id && over?.id && pujaLineReorderMode === pujaId) {
      const lines = [...(reorderedPujaLines.get(pujaId) || [])];
      const oldIndex = lines.findIndex(line => `puja-line-${line.id}` === active.id);
      const newIndex = lines.findIndex(line => `puja-line-${line.id}` === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const newLines = arrayMove(lines, oldIndex, newIndex);
        setReorderedPujaLines(new Map(reorderedPujaLines.set(pujaId, newLines)));
      }
    }
  };

  const handleTogglePujaLineReorderMode = (pujaId: number) => {
    if (pujaLineReorderMode === pujaId) {
      // Save changes
      handleSavePujaLineReorder(pujaId);
    } else {
      // Enter reorder mode
      const puja = pujas.find(p => p.id === pujaId);
      const sortedLines = puja?.lines ? [...puja.lines].sort((a, b) => (a.order || 0) - (b.order || 0)) : [];
      setReorderedPujaLines(new Map(reorderedPujaLines.set(pujaId, sortedLines)));
      setPujaLineReorderMode(pujaId);
    }
  };

  const handleSavePujaLineReorder = async (pujaId: number) => {
    try {
      const lines = reorderedPujaLines.get(pujaId);
      if (!lines) {
        setPujaLineReorderMode(null);
        return;
      }

      // Prepare bulk update payload
      const updates = lines.map((line, index) => ({
        pujaLineId: line.id!,
        changedOrder: index,
      }));

      await dispatch(bulkUpdatePujaLineOrder(updates)).unwrap();
      message.success('Line order updated successfully');
      setPujaLineReorderMode(null);
      
      // Clear reordered lines
      const newReorderedLines = new Map(reorderedPujaLines);
      newReorderedLines.delete(pujaId);
      setReorderedPujaLines(newReorderedLines);
    } catch (error) {
      console.error('Error updating line order:', error);
      message.error('Failed to update line order');
    }
  };

  const handleCancelPujaLineReorder = (pujaId: number) => {
    const newReorderedLines = new Map(reorderedPujaLines);
    newReorderedLines.delete(pujaId);
    setReorderedPujaLines(newReorderedLines);
    setPujaLineReorderMode(null);
  };

  const handleToggleReorderMode = (pujaId: number, lineId: number, position: number) => {
    const key = `${pujaId}-${lineId}-${position}`;
    const currentKey = reorderMode ? `${reorderMode.pujaId}-${reorderMode.lineId}-${position}` : null;
    
    if (reorderMode && currentKey === key) {
      // Save changes
      handleSaveReorder(pujaId, lineId, position);
    } else {
      // Enter reorder mode
      const puja = pujas.find(p => p.id === pujaId);
      const line = puja?.lines?.find(l => l.id === lineId);
      const linksAtPosition = (line?.bidhiLinks || []).filter(link => link.position === position);
      const sortedLinks = [...linksAtPosition].sort((a, b) => (a.order || 0) - (b.order || 0));
      setReorderedLinks(new Map(reorderedLinks.set(key, sortedLinks)));
      setReorderMode({ pujaId, lineId });
    }
  };

  const handleSaveReorder = async (pujaId: number, lineId: number, position: number) => {
    try {
      const key = `${pujaId}-${lineId}-${position}`;
      const links = reorderedLinks.get(key);
      if (!links) {
        setReorderMode(null);
        return;
      }

      const updates = links.map((link, index) => ({
        pujaBidhiLinkId: link.id,
        changedOrder: index
      }));

      await pujaLineLinkApi.bulkUpdateBidhiLinkOrder(updates);
      // Update state directly instead of fetching
      dispatch(updatePujaBidhiLinkOrder({ pujaLineId: lineId, position, updates }));
      message.success('Link order updated successfully');
      setReorderMode(null);
      // Clear reordered links
      const newReorderedLinks = new Map(reorderedLinks);
      newReorderedLinks.delete(key);
      setReorderedLinks(newReorderedLinks);
    } catch (error) {
      console.error('Error updating link order:', error);
      message.error('Failed to update link order');
    }
  };

  const handleCancelReorder = (pujaId: number, lineId: number, position: number) => {
    const key = `${pujaId}-${lineId}-${position}`;
    const newReorderedLinks = new Map(reorderedLinks);
    newReorderedLinks.delete(key);
    setReorderedLinks(newReorderedLinks);
    setReorderMode(null);
  };

  const handleDeleteSankalpaLink = async (linkId: number) => {
    try {
      await pujaLineLinkApi.deleteSankalpaLink(linkId);
      dispatch(removePujaSankalpaLink({ linkId }));
      message.success('Sankalpa link detached successfully');
    } catch (error) {
      console.error('Error deleting sankalpa link:', error);
      message.error('Failed to detach sankalpa link');
    }
  };

  const loadPujaTags = async () => {
    setLoadingPujaTags(true);
    try {
      const response = await pujaTagAPI.getAll();
      setPujaTags(response.data);
    } catch (error) {
      console.error('Error loading puja tags:', error);
    } finally {
      setLoadingPujaTags(false);
    }
  };

  const handlePujaTagSearch = (value: string) => {
    setPujaTagSearchValue(value);
  };

  const handleCreatePujaTag = async (fieldName?: number) => {
    const fieldToSet = fieldName !== undefined ? fieldName : activePujaTagFieldName;
    if (pujaTagSearchValue.trim()) {
      setCreatingPujaTag(true);
      try {
        const response = await pujaTagAPI.createTag({ name: pujaTagSearchValue.trim() });
        setPujaTags(prev => [...prev, response.data]);
        if (fieldToSet !== null) {
          // Set the newly created tag in the active field
          editPujaForm.setFieldValue(['tagIds', fieldToSet], response.data.id);
        }
        setPujaTagSearchValue('');
        setActivePujaTagFieldName(null);
        message.success('Tag created successfully');
      } catch (error) {
        console.error('Error creating tag:', error);
        message.error('Failed to create tag');
      } finally {
        setCreatingPujaTag(false);
      }
    }
  };

  const handleRemovePujaTag = async (tagId: number) => {
    if (!selectedPujaId) return;
    try {
      await pujaTagAPI.removeTagFromPuja(tagId, selectedPujaId);
      message.success('Tag removed successfully');
      // Refresh pujas to update tags
      dispatch(fetchPujas());
    } catch (error) {
      console.error('Error removing tag:', error);
      message.error('Failed to remove tag');
    }
  };

  useEffect(() => {
    if (editPujaModalVisible) {
      loadPujaTags();
    }
  }, [editPujaModalVisible]);

  const handleEditPujaClick = (puja: any) => {
    setSelectedPujaId(puja.id);
    const customMetadata = puja.metadata ? Object.entries(puja.metadata)
      .filter(([key]) => !['bidha', 'upabidha', 'prakar', 'upaprakar', 'aupaprakar'].includes(key))
      .map(([key, value]) => ({ key, value })) : [];
    
    editPujaForm.setFieldsValue({
      name: puja.name,
      bidha: puja.metadata?.bidha || '',
      upabidha: puja.metadata?.upabidha || '',
      prakar: puja.metadata?.prakar || '',
      upaprakar: puja.metadata?.upaprakar || '',
      aupaprakar: puja.metadata?.aupaprakar || '',
      customMetadata,
      tagIds: puja.tags?.map((tag: any) => tag.id) || [],
    });
    
    // Set existing profile picture if available
    if (puja.profilePicture) {
      setEditPujaFileList([{
        uid: '-1',
        name: puja.profilePicture,
        status: 'done',
        url: `${API_URL}/uploads/pujas/${puja.profilePicture}`,
      }]);
    } else {
      setEditPujaFileList([]);
    }
    
    setEditPujaModalVisible(true);
  };

  const handleEditLineClick = (puja: any, line: PujaLine) => {
    setSelectedPujaId(puja.id);
    setSelectedLineId(line.id!);
    const metadataArray =
      line.metadata
        ? Object.entries(line.metadata).map(([key, value]) => ({ key, value }))
        : [];

    editLineForm.setFieldsValue({
      content: line.content,
      order: line.order,
      isVisible: line.isVisible ?? true,
      metadata: metadataArray,
    });
    setEditLineModalVisible(true);
  };

  const tagOptions = useMemo(() => {
    const map = new Map<number, string>();
    (pujas || []).forEach((p: any) => {
      (p.tags || []).forEach((t: any) => {
        if (t?.id != null && typeof t?.name === 'string') map.set(Number(t.id), t.name);
      });
    });
    return Array.from(map.entries())
      .sort((a, b) => a[1].localeCompare(b[1]))
      .map(([id, name]) => ({ value: id, label: name }));
  }, [pujas]);

  const filteredPujas = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const tagSet = new Set(tagFilterIds);
    return (pujas || []).filter((p: any) => {
      const name = String(p?.name || '').toLowerCase();
      const desc = String(p?.metadata?.description || '').toLowerCase();
      const tagNames = (p?.tags || []).map((t: any) => String(t?.name || '').toLowerCase());

      const matchesText = !q || name.includes(q) || desc.includes(q) || tagNames.some((tn: string) => tn.includes(q));
      const matchesTag = tagSet.size === 0 || (p?.tags || []).some((t: any) => tagSet.has(Number(t?.id)));
      return matchesText && matchesTag;
    });
  }, [pujas, searchQuery, tagFilterIds]);

  return (
    <div>
      <div
        style={{
          marginBottom: 16,
          display: 'flex',
          justifyContent: 'space-between',
          position: 'sticky',
          top: 0,
          // Keep above page content, but below AntD modals (which use much higher z-index).
          zIndex: 10,
          background: '#fff',
          paddingTop: 8,
          paddingBottom: 8,
        }}
      >
        <h2>Puja Management</h2>
        <Space wrap>
          <Button
            type="default"
            style={{ position: 'relative' }}
            onClick={async () => {
              // Always open the modal immediately (even if backend APIs fail).
              setBookDefaultsModalOpen(true);
              const localBase = loadBookDefaultStyles();
              setSelectedStyleTemplateId(null);
              setSelectedStyleTemplateBase(localBase);
              bookDefaultsForm.setFieldsValue(bookDefaultsToFormValues(localBase));
              bookDefaultsForm.setFieldValue('templateName', '');

              try {
                const { templates, activeId } = await refreshStyleTemplates();
                const activeTemplate = activeId != null ? templates.find((t: any) => Number(t.id) === Number(activeId)) : undefined;
                const base = activeTemplate?.styles || DEFAULT_BOOK_STYLES;
                setSelectedStyleTemplateId(activeTemplate?.id ?? null);
                setSelectedStyleTemplateBase(base);
                bookDefaultsForm.setFieldsValue(bookDefaultsToFormValues(base));
                bookDefaultsForm.setFieldValue('templateName', typeof activeTemplate?.name === 'string' ? activeTemplate.name : '');
              } catch (e) {
                // Keep local defaults as fallback so user can still edit.
                message.warning('Backend templates not available yet. Showing local defaults.');
              }
            }}
          >
            Book default styles
          </Button>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setAddPujaModalVisible(true)}
        >
          Add Puja
        </Button>
        </Space>
      </div>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 }}>
        <Input
          placeholder="Search by name, description, or tag..."
          value={searchQuery}
          onChange={(e: any) => {
            const val = e.target.value;
            setSearchQuery(val);
            const params = new URLSearchParams(searchParams);
            if (val) params.set('q', val);
            else params.delete('q');
            setSearchParams(params);
          }}
          allowClear
          style={{ width: 320, maxWidth: '100%' }}
        />
        <Select
          mode="multiple"
          allowClear
          placeholder="Filter by tag"
          value={tagFilterIds}
          onChange={(vals: any) => {
            const ids = vals as number[];
            setTagFilterIds(ids);
            const params = new URLSearchParams(searchParams);
            if (ids.length > 0) params.set('tags', ids.join(','));
            else params.delete('tags');
            setSearchParams(params);
          }}
          options={tagOptions}
          style={{ minWidth: 260, maxWidth: '100%' }}
          maxTagCount="responsive"
        />
        <div style={{ color: '#666', fontSize: 12 }}>
          Showing <strong>{filteredPujas.length}</strong> / {pujas.length}
        </div>
      </div>
      <Card loading={loading} variant="borderless" style={{ background: 'transparent' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, 280px)',
            gap: 24,
            justifyContent: 'center',
          }}
        >
          {filteredPujas.map((puja) => (
            <Card
              key={puja.id?.toString() || ''}
              hoverable
              style={{
                width: 280,
                padding: 0,
                borderRadius: 16,
                overflow: 'hidden',
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                transition: 'all 0.3s ease',
                border: '1px solid rgba(0,0,0,0.06)',
              }}
              styles={{
                body: {
                  padding: 0
                }
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.12)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
              }}
            >
                <div
                  style={{ 
                    position: 'relative', 
                    aspectRatio: '4/5', 
                    background: puja.profilePicture ? 'transparent' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
                    cursor: 'pointer',
                    overflow: 'hidden'
                  }}
                  onClick={() => {
                    const params = new URLSearchParams();
                    if (searchQuery) params.set('q', searchQuery);
                    if (tagFilterIds.length > 0) params.set('tags', tagFilterIds.join(','));
                    const queryString = params.toString();
                    navigate(`/pujaView/${puja.id}${queryString ? '?' + queryString : ''}`);
                  }}
                >
                  {/* Action buttons - top corners */}
                  <div style={{ position: 'absolute', top: 12, left: 12, zIndex: 10, display: 'flex', gap: 6 }}>
                    <Button
                      type="default"
                      size="small"
                      icon={<EditOutlined />}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/pujaEdit/${puja.id}`);
                      }}
                      style={{ 
                        background: 'rgba(255,255,255,0.95)', 
                        color: '#1890ff', 
                        border: 'none', 
                        backdropFilter: 'blur(8px)',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        borderRadius: '8px'
                      }}
                    />
                    <Popconfirm
                      title="Duplicate puja?"
                      description="Are you sure you want to create a copy of this puja?"
                      onConfirm={(e) => {
                        e?.stopPropagation();
                        handleDuplicatePuja(puja);
                      }}
                      onCancel={(e) => {
                        e?.stopPropagation();
                      }}
                      okText="Yes"
                      cancelText="No"
                    >
                      <Button
                        type="default"
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                        loading={duplicatingPujaId === puja.id}
                        disabled={duplicatingPujaId !== null && duplicatingPujaId !== puja.id}
                        style={{ 
                          background: 'rgba(255,255,255,0.95)', 
                          color: '#52c41a', 
                          border: 'none', 
                          backdropFilter: 'blur(8px)',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                          borderRadius: '8px'
                        }}
                      />
                    </Popconfirm>
                  </div>
                  
                  <Popconfirm
                    title="Delete puja?"
                    description="Are you sure you want to delete this puja?"
                    onConfirm={() => handleDeletePuja(puja.id!)}
                    okText="Yes"
                    cancelText="No"
                    okButtonProps={{ danger: true }}
                  >
                    <Button
                      type="text"
                      danger
                      icon={<CloseOutlined />}
                      size="small"
                      style={{ 
                        position: 'absolute', 
                        top: 12, 
                        right: 12, 
                        zIndex: 10, 
                        background: 'rgba(255,255,255,0.95)', 
                        backdropFilter: 'blur(8px)',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        borderRadius: '8px'
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </Popconfirm>

                  {puja.profilePicture ? (
                    <Image
                      src={`${API_URL}/uploads/pujas/${puja.profilePicture}`}
                      alt={puja.name}
                      style={{ 
                        width: '100%', 
                        height: '100%', 
                        objectFit: 'cover',
                        transition: 'transform 0.3s ease'
                      }}
                      preview={false}
                    />
                  ) : (
                    <div style={{ 
                      width: '100%', 
                      height: '100%', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      color: 'rgba(255,255,255,0.8)', 
                      fontSize: '16px',
                      fontWeight: 500
                    }}>
                      {puja.name}
                    </div>
                  )}

                  {/* Top overlay with branding */}
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    background: 'linear-gradient(to bottom, rgba(0,0,0,0.5) 0%, transparent 100%)',
                    padding: '52px 16px 32px',
                    zIndex: 2,
                    pointerEvents: 'none'
                  }}>
                    <div style={{ 
                      color: 'white', 
                      fontSize: '11px', 
                      fontWeight: 600, 
                      marginBottom: 4, 
                      letterSpacing: '0.5px',
                      textShadow: '0 2px 4px rgba(0,0,0,0.3)' 
                    }}>
                      धर्मकर्म
                    </div>
                    {puja.metadata?.bidha && (
                      <div style={{ 
                        color: 'rgba(255,255,255,0.9)', 
                        fontSize: '10px', 
                        fontWeight: 400, 
                        textShadow: '0 1px 3px rgba(0,0,0,0.4)', 
                        lineHeight: '1.4' 
                      }}>
                        {puja.metadata.bidha}
                      </div>
                    )}
                  </div>
                </div>

                {/* Card content below image */}
                <div style={{ padding: '16px' }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px',
                    marginBottom: '8px'
                  }}>
                    <span style={{
                      color: '#8c8c8c',
                      fontSize: '11px',
                      fontWeight: 500,
                      letterSpacing: '0.3px'
                    }}>
                      #{puja.id}
                    </span>
                    {puja.metadata?.bidha && (
                      <>
                        <span style={{ color: '#d9d9d9' }}>•</span>
                        <span style={{
                          color: '#8c8c8c',
                          fontSize: '11px',
                          fontWeight: 400
                        }}>
                          {puja.metadata.bidha}
                        </span>
                      </>
                    )}
                  </div>
                  <Typography.Title 
                    level={5} 
                    style={{ 
                      margin: 0, 
                      color: '#262626', 
                      fontSize: '15px', 
                      fontWeight: 600,
                      lineHeight: '1.4',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      marginBottom: puja.tags && puja.tags.length > 0 ? '10px' : '0'
                    }}
                  >
                    {puja.name}
                  </Typography.Title>
                  {puja.tags && puja.tags.length > 0 && (
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {puja.tags.slice(0, 3).map((tag: any) => (
                        <Tag key={tag.id} style={{ 
                          margin: 0, 
                          fontSize: '11px', 
                          padding: '2px 10px', 
                          background: '#f0f0f0', 
                          color: '#595959', 
                          border: '1px solid #d9d9d9',
                          borderRadius: '4px',
                          fontWeight: 500
                        }}>
                          {tag.name}
                        </Tag>
                      ))}
                      {puja.tags.length > 3 && (
                        <Tag style={{ 
                          margin: 0, 
                          fontSize: '11px', 
                          padding: '2px 10px', 
                          background: '#fafafa', 
                          color: '#8c8c8c', 
                          border: '1px solid #d9d9d9',
                          borderRadius: '4px',
                          fontWeight: 500
                        }}>
                          +{puja.tags.length - 3}
                        </Tag>
                      )}
                    </div>
                  )}
                </div>
            </Card>
          ))}
        </div>
      </Card>

      <AddPujaModal
        visible={addPujaModalVisible}
        onCancel={() => setAddPujaModalVisible(false)}
        onSubmit={handleAddPuja}
      />

      <Modal
        title="Book default style templates"
        open={bookDefaultsModalOpen}
        centered
        onCancel={() => setBookDefaultsModalOpen(false)}
        width={1100}
        style={{ maxWidth: '90vw' }}
        footer={[
          <Button key="cancelDefaults" onClick={() => setBookDefaultsModalOpen(false)}>
            Close
          </Button>,
          <Button
            key="clearDraftStyles"
            onClick={() => {
              const cleared = formValuesToBookDefaults(
                {
                  ...bookDefaultsForm.getFieldsValue(),
                  defaultTextColor: '',
                  defaultBackgroundColor: '',
                  defaultLinkColor: '',
                  defaultHeaderStyle: { align: 'left', italic: false, underline: false },
                  defaultLineStyle: { align: 'left', bold: false, italic: false, underline: false },
                  defaultNestedLineStyle: { align: 'left', bold: false, italic: false, underline: false },
                  defaultDeepNestedLineStyle: { align: 'left', bold: false, italic: false, underline: false },
                  defaultLinkStyle: {},
                },
                selectedStyleTemplateBase as any
              );
              setSelectedStyleTemplateBase(cleared as any);
              bookDefaultsForm.setFieldsValue(bookDefaultsToFormValues(cleared as any));
              message.success('Draft styles cleared (not saved yet)');
            }}
          >
            Clear draft styles
          </Button>,
          <Button
            key="updateTemplate"
            type="primary"
            loading={styleTemplatesLoading}
            onClick={async () => {
              try {
                await handleSaveTemplate();
              } catch {
                message.error('Failed to save template');
              }
            }}
          >
            Save
          </Button>,
        ]}
      >
        <Form
          form={bookDefaultsForm}
          layout="vertical"
          scrollToFirstError
          onFinish={(vals) => { /*
            const cleanStringOrUndef = (v: any) => (typeof v === 'string' && v.trim() ? v.trim() : undefined);
            const cleanNumberOrUndef = (v: any) => (v != null && Number.isFinite(Number(v)) ? Number(v) : undefined);
            const cleanPositiveOrUndef = (v: any) => {
              const n = cleanNumberOrUndef(v);
              return n != null && n > 0 ? n : undefined;
            };
            const cleanStyle = (s: any, mode: 'header' | 'line') => {
              if (!s || typeof s !== 'object') return undefined;
              const textAlign = s.align || s.textAlign;
              const span = s.span === 12 || s.span === 24 ? s.span : undefined;
              const base: any = {
                fontSize: cleanPositiveOrUndef(s.fontSize),
                color: cleanStringOrUndef(s.color),
                backgroundColor: cleanStringOrUndef(s.backgroundColor),
                fontFamily: cleanStringOrUndef(s.fontFamily),
                fontWeight:
                  mode === 'line'
                    ? (s.bold ? 700 : 400)
                    : (cleanNumberOrUndef(s.fontWeight) ?? undefined),
                fontStyle: s.italic ? ('italic' as const) : undefined,
                textDecoration: s.underline ? 'underline' : undefined,
                textAlign: (textAlign === 'left' || textAlign === 'center' || textAlign === 'right' || textAlign === 'justify') ? textAlign : undefined,
                borderWidth: cleanPositiveOrUndef(s.borderWidth),
                borderStyle:
                  (s.borderStyle === 'solid' || s.borderStyle === 'dashed' || s.borderStyle === 'dotted' || s.borderStyle === 'double')
                    ? s.borderStyle
                    : undefined,
                borderColor: cleanStringOrUndef(s.borderColor),
                borderRadius: cleanPositiveOrUndef(s.borderRadius),
                padding: cleanPositiveOrUndef(s.padding),
                paddingLeft: cleanPositiveOrUndef(s.paddingLeft),
                paddingRight: cleanPositiveOrUndef(s.paddingRight),
                paddingTop: cleanPositiveOrUndef(s.paddingTop),
                paddingBottom: cleanPositiveOrUndef(s.paddingBottom),
                margin: cleanPositiveOrUndef(s.margin),
                marginLeft: cleanPositiveOrUndef(s.marginLeft),
                marginRight: cleanPositiveOrUndef(s.marginRight),
                marginTop: cleanPositiveOrUndef(s.marginTop),
                marginBottom: cleanPositiveOrUndef(s.marginBottom),
              };
              if (mode === 'line' && span) base.span = span;
              return base;
            };

            const cur = loadBookDefaultStyles();
            const numOr = (v: any, fallback: number) => {
              const n = Number(v);
              return Number.isFinite(n) ? n : fallback;
            };
            const strOr = (v: any, fallback: string) => (typeof v === 'string' ? v : fallback);

            const next = {
              version: 1 as const,
              pagePaddingPx: numOr(vals.pagePaddingPx, cur.pagePaddingPx),
              baseFontSizePx: numOr(vals.baseFontSizePx, cur.baseFontSizePx),
              gridGapPx: numOr(vals.gridGapPx, cur.gridGapPx),
              headerBackgroundColor: cur.headerBackgroundColor || '',
              headerTextColor: cur.headerTextColor || '',
              titleColor: cur.titleColor || '',
              defaultTextColor: strOr(vals.defaultTextColor, cur.defaultTextColor || ''),
              defaultBackgroundColor: strOr(vals.defaultBackgroundColor, cur.defaultBackgroundColor || ''),
              defaultLinkColor: strOr(vals.defaultLinkColor, cur.defaultLinkColor || ''),
              defaultHeaderStyle: cleanStyle(vals.defaultHeaderStyle, 'header'),
              defaultLineStyle: cleanStyle(vals.defaultLineStyle, 'line'),
              defaultNestedLineStyle: cleanStyle(vals.defaultNestedLineStyle, 'line'),
              defaultDeepNestedLineStyle: cleanStyle(vals.defaultDeepNestedLineStyle, 'line'),
              // Card header/title and top tags are not editable here; preserve existing stored values.
              defaultCardHeaderStyle: cur.defaultCardHeaderStyle,
              defaultCardTitleStyle: cur.defaultCardTitleStyle,
              defaultTagStyle: cur.defaultTagStyle,
              defaultLinkStyle: cleanStyle(vals.defaultLinkStyle, 'header'),
            };
            saveBookDefaultStyles(next);
            message.success('Book defaults saved');
            setBookDefaultsModalOpen(false);
          */ }}
          initialValues={{
            templateName: '',
            saveAsNew: false,
            pagePaddingPx: DEFAULT_BOOK_STYLES.pagePaddingPx,
            baseFontSizePx: DEFAULT_BOOK_STYLES.baseFontSizePx,
            gridGapPx: DEFAULT_BOOK_STYLES.gridGapPx,
            defaultTextColor: DEFAULT_BOOK_STYLES.defaultTextColor || '',
            defaultBackgroundColor: DEFAULT_BOOK_STYLES.defaultBackgroundColor || '',
            defaultLinkColor: DEFAULT_BOOK_STYLES.defaultLinkColor || '',
            defaultHeaderStyle: {
              ...(DEFAULT_BOOK_STYLES.defaultHeaderStyle || {}),
              align: 'left',
              italic: false,
              underline: false,
            },
            defaultLineStyle: {
              ...(DEFAULT_BOOK_STYLES.defaultLineStyle || {}),
              align: 'left',
              bold: false,
              italic: false,
              underline: false,
            },
            defaultNestedLineStyle: {
              ...(DEFAULT_BOOK_STYLES.defaultNestedLineStyle || {}),
              align: 'left',
              bold: false,
              italic: false,
              underline: false,
            },
            defaultDeepNestedLineStyle: {
              ...(DEFAULT_BOOK_STYLES.defaultDeepNestedLineStyle || {}),
              align: 'left',
              bold: false,
              italic: false,
              underline: false,
            },
            defaultLinkStyle: DEFAULT_BOOK_STYLES.defaultLinkStyle || {},
          }}
        >
          <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
            These defaults affect how all Puja books render. Individual block overrides will still take priority.
          </Typography.Paragraph>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <Typography.Text>Save as new template</Typography.Text>
            <Form.Item name="saveAsNew" valuePropName="checked" style={{ marginBottom: 0 }}>
              <Switch size="small" />
            </Form.Item>
          </div>

          <Form.Item noStyle dependencies={['saveAsNew']}>
            {({ getFieldValue }) => {
              const saveAsNew = !!getFieldValue('saveAsNew');
              return saveAsNew ? (
                <Form.Item
                  name="templateName"
                  label="Template name"
                  rules={[{ required: true, whitespace: true, message: 'Template name is required' }]}
                >
                  <Input placeholder="Enter template name" style={{ width: 320 }} />
                </Form.Item>
              ) : null;
            }}
          </Form.Item>

          <Divider style={{ margin: '12px 0' }} />
          {/*
          <Form.Item noStyle shouldUpdate>
            {() => {
              const headerStyle = bookDefaultsForm.getFieldValue('defaultHeaderStyle');
              const lineStyle = bookDefaultsForm.getFieldValue('defaultLineStyle');
              const nestedLineStyle = bookDefaultsForm.getFieldValue('defaultNestedLineStyle');
              const deepNestedLineStyle = bookDefaultsForm.getFieldValue('defaultDeepNestedLineStyle');
              const linkStyle = bookDefaultsForm.getFieldValue('defaultLinkStyle');

              return (
                <div style={{ marginBottom: 12 }}>
                  <Typography.Text strong>Preview (click pencil to edit)</Typography.Text>
                  <div style={{ marginTop: 8 }}>
                    <Card size="small" title={<span style={{ opacity: 0.9 }}>Sample body</span>}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                            <span style={asPreviewCss(headerStyle)}>Header / Section</span>
                            <Button
                              size="small"
                              type="text"
                              icon={<EditOutlined />}
                              onClick={() => {
                                setBookDefaultsAdvancedVisible(true);
                                setBookDefaultsAdvancedKeys(['headerAdvanced']);
                              }}
                            />
                          </div>

                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={asPreviewCss(lineStyle)}>
                              This is a sample line with a <span style={asPreviewCss(linkStyle)}>link</span>.
                            </span>
                            <Button
                              size="small"
                              type="text"
                              icon={<EditOutlined />}
                              onClick={() => {
                                setBookDefaultsAdvancedVisible(true);
                                setBookDefaultsAdvancedKeys(['lineAdvanced', 'linkAdvanced']);
                              }}
                            />
                          </div>

                          <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={asPreviewCss(nestedLineStyle)}>Nested line (level 1)</span>
                            <Button
                              size="small"
                              type="text"
                              icon={<EditOutlined />}
                              onClick={() => {
                                setBookDefaultsAdvancedVisible(true);
                                setBookDefaultsAdvancedKeys(['nestedLineAdvanced']);
                              }}
                            />
                          </div>

                          <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8, marginLeft: 20 }}>
                            <span style={asPreviewCss(deepNestedLineStyle)}>Nested line (level 2+)</span>
                            <Button
                              size="small"
                              type="text"
                              icon={<EditOutlined />}
                              onClick={() => {
                                setBookDefaultsAdvancedVisible(true);
                                setBookDefaultsAdvancedKeys(['deepNestedLineAdvanced']);
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </Card>
                  </div>
                </div>
              );
            }}
          </Form.Item>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <Typography.Text strong>Advanced editor</Typography.Text>
            <Button
              size="small"
              onClick={() => {
                setBookDefaultsAdvancedVisible((v) => !v);
                if (!bookDefaultsAdvancedVisible) {
                  setBookDefaultsAdvancedKeys(['headerAdvanced']);
                }
              }}
            >
              {bookDefaultsAdvancedVisible ? 'Hide' : 'Show'}
            </Button>
          </div>

          <div
            style={{
              display: bookDefaultsAdvancedVisible ? 'grid' : 'none',
              gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 360px)',
              gap: 12,
              alignItems: 'start',
            }}
          >
            <Collapse
              size="small"
              activeKey={bookDefaultsAdvancedKeys}
              onChange={(keys) => setBookDefaultsAdvancedKeys(Array.isArray(keys) ? keys : [String(keys)])}
              items={[
              {
                key: 'linkAdvanced',
                label: 'Links (advanced style)',
                children: (
                  <Collapse
                    size="small"
                    defaultActiveKey={['colors']}
                    items={[
                      {
                        key: 'colors',
                        label: 'Colors',
                        children: (
                          <>
                            <Form.Item label="Text color">
                              <Space wrap>
                                <Form.Item name={['defaultLinkStyle', 'color']} noStyle>
                                  <Input placeholder="#1890ff" style={{ width: 140 }} />
                                </Form.Item>
                                <ColorPicker
                                  value={(typeof linkTextColor === 'string' && linkTextColor.trim() ? linkTextColor : undefined) || '#1890ff'}
                                  onChange={(c) => bookDefaultsForm.setFieldValue(['defaultLinkStyle', 'color'], c.toHexString())}
                                  showText
                                />
                              </Space>
                            </Form.Item>
                            <Form.Item label="Background color">
                              <Space wrap>
                                <Form.Item name={['defaultLinkStyle', 'backgroundColor']} noStyle>
                                  <Input placeholder="#ffffff" style={{ width: 140 }} />
                                </Form.Item>
                                <ColorPicker
                                  value={(typeof linkBgColor === 'string' && linkBgColor.trim() ? linkBgColor : undefined) || '#ffffff'}
                                  onChange={(c) => bookDefaultsForm.setFieldValue(['defaultLinkStyle', 'backgroundColor'], c.toHexString())}
                                  showText
                                />
                              </Space>
                            </Form.Item>
                          </>
                        ),
                      },
                      {
                        key: 'border',
                        label: 'Border',
                        children: (
                          <Space wrap>
                            <Form.Item name={['defaultLinkStyle', 'borderWidth']} label="Border width" style={{ marginBottom: 0 }}>
                              <InputNumber min={0} max={12} style={{ width: 140 }} />
                            </Form.Item>
                            <Form.Item name={['defaultLinkStyle', 'borderStyle']} label="Border style" style={{ marginBottom: 0 }}>
                              <Select
                                style={{ width: 160 }}
                                options={[
                                  { label: 'None', value: 'none' },
                                  { label: 'Solid', value: 'solid' },
                                  { label: 'Dashed', value: 'dashed' },
                                  { label: 'Dotted', value: 'dotted' },
                                  { label: 'Double', value: 'double' },
                                ]}
                              />
                            </Form.Item>
                            <Form.Item label="Border color" style={{ marginBottom: 0 }}>
                              <Space wrap>
                                <Form.Item name={['defaultLinkStyle', 'borderColor']} noStyle>
                                  <Input placeholder="#d9d9d9" style={{ width: 140 }} />
                                </Form.Item>
                                <ColorPicker
                                  value={(typeof linkBorderColor === 'string' && linkBorderColor.trim() ? linkBorderColor : undefined) || '#d9d9d9'}
                                  onChange={(c) => bookDefaultsForm.setFieldValue(['defaultLinkStyle', 'borderColor'], c.toHexString())}
                                  showText
                                />
                              </Space>
                            </Form.Item>
                          </Space>
                        ),
                      },
                    ]}
                  />
                ),
              },
              {
                key: 'headerAdvanced',
                label: 'Header (advanced style)',
                children: (
                  <Collapse
                    size="small"
                    defaultActiveKey={['colors', 'typography']}
                    items={[
                      {
                        key: 'colors',
                        label: 'Colors',
                        children: (
                          <>
                            <Form.Item label="Text color">
                              <Space wrap>
                                <Form.Item name={['defaultHeaderStyle', 'color']} noStyle>
                                  <Input placeholder="#111111" style={{ width: 140 }} />
                                </Form.Item>
                                <ColorPicker
                                  value={typeof bookHeaderTextColor === 'string' && bookHeaderTextColor.trim() ? bookHeaderTextColor : undefined}
                                  onChange={(c) => bookDefaultsForm.setFieldValue(['defaultHeaderStyle', 'color'], c.toHexString())}
                                  showText
                                />
                              </Space>
                            </Form.Item>
                            <Form.Item label="Background color">
                              <Space wrap>
                                <Form.Item name={['defaultHeaderStyle', 'backgroundColor']} noStyle>
                                  <Input placeholder="#ffffff" style={{ width: 140 }} />
                                </Form.Item>
                                <ColorPicker
                                  value={typeof bookHeaderBgColor === 'string' && bookHeaderBgColor.trim() ? bookHeaderBgColor : undefined}
                                  onChange={(c) => bookDefaultsForm.setFieldValue(['defaultHeaderStyle', 'backgroundColor'], c.toHexString())}
                                  showText
                                />
                              </Space>
                            </Form.Item>
                          </>
                        ),
                      },
                      {
                        key: 'typography',
                        label: 'Typography',
                        children: (
                          <>
                            <Form.Item name={['defaultHeaderStyle', 'fontSize']} label="Font size">
                              <InputNumber min={10} max={72} style={{ width: '100%' }} />
                            </Form.Item>
                            <Form.Item name={['defaultHeaderStyle', 'fontFamily']} label="Font type (family)">
                              <Select
                                allowClear
                                placeholder="Default"
                                options={[
                                  { label: 'Default', value: '' },
                                  { label: 'Vag', value: 'Vag' },
                                  { label: 'Vag-Bold', value: 'Vag-Bold' },
                                  { label: 'Serif', value: 'serif' },
                                  { label: 'Sans', value: 'sans-serif' },
                                  { label: 'Monospace', value: 'monospace' },
                                ]}
                              />
                            </Form.Item>
                            <Form.Item name={['defaultHeaderStyle', 'fontWeight']} label="Font weight">
                              <Select
                                options={[
                                  { label: 'Normal (400)', value: 400 },
                                  { label: 'SemiBold (600)', value: 600 },
                                  { label: 'Bold (700)', value: 700 },
                                ]}
                              />
                            </Form.Item>
                            <Space wrap style={{ marginBottom: 12 }}>
                              <Form.Item name={['defaultHeaderStyle', 'italic']} style={{ marginBottom: 0 }}>
                                <Select
                                  style={{ width: 140 }}
                                  options={[
                                    { label: 'Normal', value: false },
                                    { label: 'Italic', value: true },
                                  ]}
                                />
                              </Form.Item>
                              <Form.Item name={['defaultHeaderStyle', 'underline']} style={{ marginBottom: 0 }}>
                                <Select
                                  style={{ width: 160 }}
                                  options={[
                                    { label: 'No underline', value: false },
                                    { label: 'Underline', value: true },
                                  ]}
                                />
                              </Form.Item>
                            </Space>
                          </>
                        ),
                      },
                      {
                        key: 'border',
                        label: 'Border',
                        children: (
                          <Space wrap>
                            <Form.Item name={['defaultHeaderStyle', 'borderWidth']} label="Border width" style={{ marginBottom: 0 }}>
                              <InputNumber min={0} max={12} style={{ width: 140 }} />
                            </Form.Item>
                            <Form.Item name={['defaultHeaderStyle', 'borderStyle']} label="Border style" style={{ marginBottom: 0 }}>
                              <Select
                                style={{ width: 160 }}
                                options={[
                                  { label: 'None', value: 'none' },
                                  { label: 'Solid', value: 'solid' },
                                  { label: 'Dashed', value: 'dashed' },
                                  { label: 'Dotted', value: 'dotted' },
                                  { label: 'Double', value: 'double' },
                                ]}
                              />
                            </Form.Item>
                            <Form.Item label="Border color" style={{ marginBottom: 0 }}>
                              <Space wrap>
                                <Form.Item name={['defaultHeaderStyle', 'borderColor']} noStyle>
                                  <Input placeholder="#d9d9d9" style={{ width: 140 }} />
                                </Form.Item>
                                <ColorPicker
                                  value={typeof bookHeaderBorderColor === 'string' && bookHeaderBorderColor.trim() ? bookHeaderBorderColor : undefined}
                                  onChange={(c) => bookDefaultsForm.setFieldValue(['defaultHeaderStyle', 'borderColor'], c.toHexString())}
                                  showText
                                />
                              </Space>
                            </Form.Item>
                            <Form.Item name={['defaultHeaderStyle', 'borderRadius']} label="Border radius" style={{ marginBottom: 0 }}>
                              <InputNumber min={0} max={24} style={{ width: 160 }} />
                            </Form.Item>
                          </Space>
                        ),
                      },
                      {
                        key: 'spacing',
                        label: 'Spacing',
                        children: (
                          <Space wrap>
                            <Form.Item name={['defaultHeaderStyle', 'padding']} label="Padding (all)" style={{ marginBottom: 0 }}>
                              <InputNumber min={0} max={64} style={{ width: 180 }} />
                            </Form.Item>
                            <Form.Item name={['defaultHeaderStyle', 'paddingLeft']} label="Padding left" style={{ marginBottom: 0 }}>
                              <InputNumber min={0} max={64} style={{ width: 180 }} />
                            </Form.Item>
                            <Form.Item name={['defaultHeaderStyle', 'paddingRight']} label="Padding right" style={{ marginBottom: 0 }}>
                              <InputNumber min={0} max={64} style={{ width: 180 }} />
                            </Form.Item>
                            <Form.Item name={['defaultHeaderStyle', 'paddingTop']} label="Padding top" style={{ marginBottom: 0 }}>
                              <InputNumber min={0} max={64} style={{ width: 180 }} />
                            </Form.Item>
                            <Form.Item name={['defaultHeaderStyle', 'paddingBottom']} label="Padding bottom" style={{ marginBottom: 0 }}>
                              <InputNumber min={0} max={64} style={{ width: 180 }} />
                            </Form.Item>
                            <Form.Item name={['defaultHeaderStyle', 'margin']} label="Margin (all)" style={{ marginBottom: 0 }}>
                              <InputNumber min={0} max={64} style={{ width: 180 }} />
                            </Form.Item>
                            <Form.Item name={['defaultHeaderStyle', 'marginLeft']} label="Margin left" style={{ marginBottom: 0 }}>
                              <InputNumber min={0} max={64} style={{ width: 180 }} />
                            </Form.Item>
                            <Form.Item name={['defaultHeaderStyle', 'marginRight']} label="Margin right" style={{ marginBottom: 0 }}>
                              <InputNumber min={0} max={64} style={{ width: 180 }} />
                            </Form.Item>
                            <Form.Item name={['defaultHeaderStyle', 'marginTop']} label="Margin top" style={{ marginBottom: 0 }}>
                              <InputNumber min={0} max={64} style={{ width: 180 }} />
                            </Form.Item>
                            <Form.Item name={['defaultHeaderStyle', 'marginBottom']} label="Margin bottom" style={{ marginBottom: 0 }}>
                              <InputNumber min={0} max={64} style={{ width: 180 }} />
                            </Form.Item>
                          </Space>
                        ),
                      },
                      {
                        key: 'layout',
                        label: 'Layout',
                        children: (
                          <Form.Item name={['defaultHeaderStyle', 'align']} label="Text align" initialValue="left">
                            <Select
                              options={[
                                { label: 'Left', value: 'left' },
                                { label: 'Center', value: 'center' },
                                { label: 'Right', value: 'right' },
                                { label: 'Justify', value: 'justify' },
                              ]}
                            />
                          </Form.Item>
                        ),
                      },
                    ]}
                  />
                ),
              },
              {
                key: 'lineAdvanced',
                label: 'Lines (advanced style)',
                children: (
                  <Collapse
                    size="small"
                    defaultActiveKey={['text', 'colors', 'typography']}
                    items={[
                      {
                        key: 'image',
                        label: 'Image',
                        children: (
                          <>
                            <Form.Item
                              name={['defaultLineStyle', 'imageSrc']}
                              label="Image source"
                              help="(Global default) Image blocks are not styled from here. Edit per-book/per-block in the Puja view."
                            >
                              <Input placeholder="https://... or data:image/..." disabled />
                            </Form.Item>
                            <Form.Item name={['defaultLineStyle', 'objectFit']} label="Image fit">
                              <Select
                                allowClear
                                placeholder="Auto"
                                disabled
                                options={[
                                  { label: 'Auto', value: '' },
                                  { label: 'Contain', value: 'contain' },
                                  { label: 'Cover', value: 'cover' },
                                ]}
                              />
                            </Form.Item>
                          </>
                        ),
                      },
                      {
                        key: 'text',
                        label: 'Text',
                        children: (
                          <Form.Item
                            name={['defaultLineStyle', 'content']}
                            label="Text"
                            help="(Global default) This is a preview-only field. Use the style panels below to set default appearance."
                          >
                            <Input.TextArea
                              placeholder="Edit text..."
                              disabled
                              autoSize={{ minRows: 3, maxRows: 6 }}
                            />
                          </Form.Item>
                        ),
                      },
                      {
                        key: 'colors',
                        label: 'Colors',
                        children: (
                          <>
                            <Form.Item label="Text color">
                              <Space wrap>
                                <Form.Item name={['defaultLineStyle', 'color']} noStyle>
                                  <Input placeholder="#ff0000" style={{ width: 140 }} />
                                </Form.Item>
                                <ColorPicker
                                  value={(typeof bookLineTextColor === 'string' && bookLineTextColor.trim() ? bookLineTextColor : undefined) || '#000000'}
                                  onChange={(c) => bookDefaultsForm.setFieldValue(['defaultLineStyle', 'color'], c.toHexString())}
                                  showText
                                />
                              </Space>
                            </Form.Item>
                            <Form.Item label="Background color">
                              <Space wrap>
                                <Form.Item name={['defaultLineStyle', 'backgroundColor']} noStyle>
                                  <Input placeholder="#fff1b8" style={{ width: 140 }} />
                                </Form.Item>
                                <ColorPicker
                                  value={(typeof bookLineBgColor === 'string' && bookLineBgColor.trim() ? bookLineBgColor : undefined) || '#ffffff'}
                                  onChange={(c) => bookDefaultsForm.setFieldValue(['defaultLineStyle', 'backgroundColor'], c.toHexString())}
                                  showText
                                />
                              </Space>
                            </Form.Item>
                          </>
                        ),
                      },
                      {
                        key: 'typography',
                        label: 'Text style',
                        children: (
                          <>
                            <Form.Item name={['defaultLineStyle', 'fontSize']} label="Font size">
                              <InputNumber min={10} max={72} style={{ width: '100%' }} />
                            </Form.Item>
                            <Form.Item name={['defaultLineStyle', 'fontFamily']} label="Font type (family)">
                              <Select
                                allowClear
                                placeholder="Default"
                                options={[
                                  { label: 'Default', value: '' },
                                  { label: 'Vag', value: 'Vag' },
                                  { label: 'Vag-Bold', value: 'Vag-Bold' },
                                  { label: 'Serif', value: 'serif' },
                                  { label: 'Sans', value: 'sans-serif' },
                                  { label: 'Monospace', value: 'monospace' },
                                ]}
                              />
                            </Form.Item>
                            <Space wrap style={{ marginBottom: 12 }}>
                              <Form.Item name={['defaultLineStyle', 'bold']} style={{ marginBottom: 0 }}>
                                <Select
                                  style={{ width: 140 }}
                                  options={[
                                    { label: 'Normal', value: false },
                                    { label: 'Bold', value: true },
                                  ]}
                                />
                              </Form.Item>
                              <Form.Item name={['defaultLineStyle', 'italic']} style={{ marginBottom: 0 }}>
                                <Select
                                  style={{ width: 140 }}
                                  options={[
                                    { label: 'Normal', value: false },
                                    { label: 'Italic', value: true },
                                  ]}
                                />
                              </Form.Item>
                              <Form.Item name={['defaultLineStyle', 'underline']} style={{ marginBottom: 0 }}>
                                <Select
                                  style={{ width: 140 }}
                                  options={[
                                    { label: 'No underline', value: false },
                                    { label: 'Underline', value: true },
                                  ]}
                                />
                              </Form.Item>
                            </Space>
                          </>
                        ),
                      },
                      {
                        key: 'border',
                        label: 'Border',
                        children: (
                          <Space wrap>
                            <Form.Item name={['defaultLineStyle', 'borderWidth']} label="Border width" style={{ marginBottom: 0 }}>
                              <InputNumber min={0} max={12} style={{ width: 140 }} />
                            </Form.Item>
                            <Form.Item name={['defaultLineStyle', 'borderStyle']} label="Border style" style={{ marginBottom: 0 }}>
                              <Select
                                style={{ width: 160 }}
                                options={[
                                  { label: 'None', value: 'none' },
                                  { label: 'Solid', value: 'solid' },
                                  { label: 'Dashed', value: 'dashed' },
                                  { label: 'Dotted', value: 'dotted' },
                                  { label: 'Double', value: 'double' },
                                ]}
                              />
                            </Form.Item>
                            <Form.Item label="Border color" style={{ marginBottom: 0 }}>
                              <Space wrap>
                                <Form.Item name={['defaultLineStyle', 'borderColor']} noStyle>
                                  <Input placeholder="#d9d9d9" style={{ width: 140 }} />
                                </Form.Item>
                                <ColorPicker
                                  value={(typeof bookLineBorderColor === 'string' && bookLineBorderColor.trim() ? bookLineBorderColor : undefined) || '#d9d9d9'}
                                  onChange={(c) => bookDefaultsForm.setFieldValue(['defaultLineStyle', 'borderColor'], c.toHexString())}
                                  showText
                                />
                              </Space>
                            </Form.Item>
                            <Form.Item name={['defaultLineStyle', 'borderRadius']} label="Border radius" style={{ marginBottom: 0 }}>
                              <InputNumber min={0} max={24} style={{ width: 160 }} />
                            </Form.Item>
                          </Space>
                        ),
                      },
                      {
                        key: 'spacing',
                        label: 'Spacing',
                        children: (
                          <Space wrap>
                            <Form.Item name={['defaultLineStyle', 'padding']} label="Padding (all)" style={{ marginBottom: 0 }}>
                              <InputNumber min={0} max={64} style={{ width: 180 }} />
                            </Form.Item>
                            <Form.Item name={['defaultLineStyle', 'paddingLeft']} label="Padding left" style={{ marginBottom: 0 }}>
                              <InputNumber min={0} max={64} style={{ width: 180 }} />
                            </Form.Item>
                            <Form.Item name={['defaultLineStyle', 'paddingRight']} label="Padding right" style={{ marginBottom: 0 }}>
                              <InputNumber min={0} max={64} style={{ width: 180 }} />
                            </Form.Item>
                            <Form.Item name={['defaultLineStyle', 'paddingTop']} label="Padding top" style={{ marginBottom: 0 }}>
                              <InputNumber min={0} max={64} style={{ width: 180 }} />
                            </Form.Item>
                            <Form.Item name={['defaultLineStyle', 'paddingBottom']} label="Padding bottom" style={{ marginBottom: 0 }}>
                              <InputNumber min={0} max={64} style={{ width: 180 }} />
                            </Form.Item>
                            <Form.Item name={['defaultLineStyle', 'margin']} label="Margin (all)" style={{ marginBottom: 0 }}>
                              <InputNumber min={0} max={64} style={{ width: 180 }} />
                            </Form.Item>
                            <Form.Item name={['defaultLineStyle', 'marginLeft']} label="Margin left" style={{ marginBottom: 0 }}>
                              <InputNumber min={0} max={64} style={{ width: 180 }} />
                            </Form.Item>
                            <Form.Item name={['defaultLineStyle', 'marginRight']} label="Margin right" style={{ marginBottom: 0 }}>
                              <InputNumber min={0} max={64} style={{ width: 180 }} />
                            </Form.Item>
                            <Form.Item name={['defaultLineStyle', 'marginTop']} label="Margin top" style={{ marginBottom: 0 }}>
                              <InputNumber min={0} max={64} style={{ width: 180 }} />
                            </Form.Item>
                            <Form.Item name={['defaultLineStyle', 'marginBottom']} label="Margin bottom" style={{ marginBottom: 0 }}>
                              <InputNumber min={0} max={64} style={{ width: 180 }} />
                            </Form.Item>
                          </Space>
                        ),
                      },
                      {
                        key: 'layout',
                        label: 'Layout',
                        children: (
                          <>
                            <Form.Item name={['defaultLineStyle', 'align']} label="Align" initialValue="left">
                              <Select
                                options={[
                                  { label: 'Left', value: 'left' },
                                  { label: 'Center', value: 'center' },
                                  { label: 'Right', value: 'right' },
                                  { label: 'Justify', value: 'justify' },
                                ]}
                              />
                            </Form.Item>
                            <Form.Item name={['defaultLineStyle', 'span']} label="Width" initialValue={24}>
                              <Select
                                options={[
                                  { label: 'Full width', value: 24 },
                                  { label: 'Half width', value: 12 },
                                ]}
                              />
                            </Form.Item>
                          </>
                        ),
                      },
              {
                key: 'nestedLineAdvanced',
                label: 'Nested lines (level 1)',
                children: (
                  <Collapse
                    size="small"
                    defaultActiveKey={['colors', 'typography', 'border', 'spacing', 'layout']}
                    items={[
                      {
                        key: 'colors',
                        label: 'Colors',
                        children: (
                          <>
                            <Form.Item label="Text color">
                              <Space wrap>
                                <Form.Item name={['defaultNestedLineStyle', 'color']} noStyle>
                                  <Input placeholder="#000000" style={{ width: 140 }} />
                                </Form.Item>
                                <ColorPicker
                                  value={(typeof bookNestedLineTextColor === 'string' && bookNestedLineTextColor.trim() ? bookNestedLineTextColor : undefined) || '#000000'}
                                  onChange={(c) => bookDefaultsForm.setFieldValue(['defaultNestedLineStyle', 'color'], c.toHexString())}
                                  showText
                                />
                              </Space>
                            </Form.Item>
                            <Form.Item label="Background color">
                              <Space wrap>
                                <Form.Item name={['defaultNestedLineStyle', 'backgroundColor']} noStyle>
                                  <Input placeholder="#ffffff" style={{ width: 140 }} />
                                </Form.Item>
                                <ColorPicker
                                  value={(typeof bookNestedLineBgColor === 'string' && bookNestedLineBgColor.trim() ? bookNestedLineBgColor : undefined) || '#ffffff'}
                                  onChange={(c) => bookDefaultsForm.setFieldValue(['defaultNestedLineStyle', 'backgroundColor'], c.toHexString())}
                                  showText
                                />
                              </Space>
                            </Form.Item>
                          </>
                        ),
                      },
                      {
                        key: 'typography',
                        label: 'Typography',
                        children: (
                          <>
                            <Form.Item name={['defaultNestedLineStyle', 'fontSize']} label="Font size">
                              <InputNumber min={10} max={72} style={{ width: '100%' }} />
                            </Form.Item>
                            <Form.Item name={['defaultNestedLineStyle', 'fontFamily']} label="Font family">
                              <Select
                                allowClear
                                placeholder="Default"
                                options={[
                                  { label: 'Default', value: '' },
                                  { label: 'Vag', value: 'Vag' },
                                  { label: 'Vag-Bold', value: 'Vag-Bold' },
                                  { label: 'Serif', value: 'serif' },
                                  { label: 'Sans', value: 'sans-serif' },
                                  { label: 'Monospace', value: 'monospace' },
                                ]}
                              />
                            </Form.Item>
                            <Space wrap style={{ marginBottom: 12 }}>
                              <Form.Item name={['defaultNestedLineStyle', 'bold']} style={{ marginBottom: 0 }}>
                                <Select style={{ width: 140 }} options={[{ label: 'Normal', value: false }, { label: 'Bold', value: true }]} />
                              </Form.Item>
                              <Form.Item name={['defaultNestedLineStyle', 'italic']} style={{ marginBottom: 0 }}>
                                <Select style={{ width: 140 }} options={[{ label: 'Normal', value: false }, { label: 'Italic', value: true }]} />
                              </Form.Item>
                              <Form.Item name={['defaultNestedLineStyle', 'underline']} style={{ marginBottom: 0 }}>
                                <Select style={{ width: 140 }} options={[{ label: 'None', value: false }, { label: 'Underline', value: true }]} />
                              </Form.Item>
                            </Space>
                          </>
                        ),
                      },
                      {
                        key: 'border',
                        label: 'Border',
                        children: (
                          <Space wrap>
                            <Form.Item name={['defaultNestedLineStyle', 'borderWidth']} label="Border width" style={{ marginBottom: 0 }}>
                              <InputNumber min={0} max={12} style={{ width: 140 }} />
                            </Form.Item>
                            <Form.Item name={['defaultNestedLineStyle', 'borderStyle']} label="Border style" style={{ marginBottom: 0 }}>
                              <Select
                                style={{ width: 160 }}
                                options={[
                                  { label: 'None', value: 'none' },
                                  { label: 'Solid', value: 'solid' },
                                  { label: 'Dashed', value: 'dashed' },
                                  { label: 'Dotted', value: 'dotted' },
                                  { label: 'Double', value: 'double' },
                                ]}
                              />
                            </Form.Item>
                            <Form.Item label="Border color" style={{ marginBottom: 0 }}>
                              <Space wrap>
                                <Form.Item name={['defaultNestedLineStyle', 'borderColor']} noStyle>
                                  <Input placeholder="#d9d9d9" style={{ width: 140 }} />
                                </Form.Item>
                                <ColorPicker
                                  value={(typeof bookNestedLineBorderColor === 'string' && bookNestedLineBorderColor.trim() ? bookNestedLineBorderColor : undefined) || '#d9d9d9'}
                                  onChange={(c) => bookDefaultsForm.setFieldValue(['defaultNestedLineStyle', 'borderColor'], c.toHexString())}
                                  showText
                                />
                              </Space>
                            </Form.Item>
                            <Form.Item name={['defaultNestedLineStyle', 'borderRadius']} label="Border radius" style={{ marginBottom: 0 }}>
                              <InputNumber min={0} max={24} style={{ width: 160 }} />
                            </Form.Item>
                          </Space>
                        ),
                      },
                      {
                        key: 'spacing',
                        label: 'Spacing',
                        children: (
                          <Space wrap>
                            <Form.Item name={['defaultNestedLineStyle', 'padding']} label="Padding" style={{ marginBottom: 0 }}>
                              <InputNumber min={0} max={64} style={{ width: 180 }} />
                            </Form.Item>
                            <Form.Item name={['defaultNestedLineStyle', 'margin']} label="Margin" style={{ marginBottom: 0 }}>
                              <InputNumber min={0} max={64} style={{ width: 180 }} />
                            </Form.Item>
                          </Space>
                        ),
                      },
                      {
                        key: 'layout',
                        label: 'Layout',
                        children: (
                          <Form.Item name={['defaultNestedLineStyle', 'align']} label="Align" initialValue="left">
                            <Select
                              options={[
                                { label: 'Left', value: 'left' },
                                { label: 'Center', value: 'center' },
                                { label: 'Right', value: 'right' },
                                { label: 'Justify', value: 'justify' },
                              ]}
                            />
                          </Form.Item>
                        ),
                      },
                    ]}
                  />
                ),
              },
              {
                key: 'deepNestedLineAdvanced',
                label: 'Nested lines (level 2+)',
                children: (
                  <Collapse
                    size="small"
                    defaultActiveKey={['colors', 'typography', 'border', 'spacing', 'layout']}
                    items={[
                      {
                        key: 'colors',
                        label: 'Colors',
                        children: (
                          <>
                            <Form.Item label="Text color">
                              <Space wrap>
                                <Form.Item name={['defaultDeepNestedLineStyle', 'color']} noStyle>
                                  <Input placeholder="#000000" style={{ width: 140 }} />
                                </Form.Item>
                                <ColorPicker
                                  value={(typeof bookDeepNestedLineTextColor === 'string' && bookDeepNestedLineTextColor.trim() ? bookDeepNestedLineTextColor : undefined) || '#000000'}
                                  onChange={(c) => bookDefaultsForm.setFieldValue(['defaultDeepNestedLineStyle', 'color'], c.toHexString())}
                                  showText
                                />
                              </Space>
                            </Form.Item>
                            <Form.Item label="Background color">
                              <Space wrap>
                                <Form.Item name={['defaultDeepNestedLineStyle', 'backgroundColor']} noStyle>
                                  <Input placeholder="#ffffff" style={{ width: 140 }} />
                                </Form.Item>
                                <ColorPicker
                                  value={(typeof bookDeepNestedLineBgColor === 'string' && bookDeepNestedLineBgColor.trim() ? bookDeepNestedLineBgColor : undefined) || '#ffffff'}
                                  onChange={(c) => bookDefaultsForm.setFieldValue(['defaultDeepNestedLineStyle', 'backgroundColor'], c.toHexString())}
                                  showText
                                />
                              </Space>
                            </Form.Item>
                          </>
                        ),
                      },
                      {
                        key: 'typography',
                        label: 'Typography',
                        children: (
                          <>
                            <Form.Item name={['defaultDeepNestedLineStyle', 'fontSize']} label="Font size">
                              <InputNumber min={10} max={72} style={{ width: '100%' }} />
                            </Form.Item>
                            <Form.Item name={['defaultDeepNestedLineStyle', 'fontFamily']} label="Font family">
                              <Select
                                allowClear
                                placeholder="Default"
                                options={[
                                  { label: 'Default', value: '' },
                                  { label: 'Vag', value: 'Vag' },
                                  { label: 'Vag-Bold', value: 'Vag-Bold' },
                                  { label: 'Serif', value: 'serif' },
                                  { label: 'Sans', value: 'sans-serif' },
                                  { label: 'Monospace', value: 'monospace' },
                                ]}
                              />
                            </Form.Item>
                            <Space wrap style={{ marginBottom: 12 }}>
                              <Form.Item name={['defaultDeepNestedLineStyle', 'bold']} style={{ marginBottom: 0 }}>
                                <Select style={{ width: 140 }} options={[{ label: 'Normal', value: false }, { label: 'Bold', value: true }]} />
                              </Form.Item>
                              <Form.Item name={['defaultDeepNestedLineStyle', 'italic']} style={{ marginBottom: 0 }}>
                                <Select style={{ width: 140 }} options={[{ label: 'Normal', value: false }, { label: 'Italic', value: true }]} />
                              </Form.Item>
                              <Form.Item name={['defaultDeepNestedLineStyle', 'underline']} style={{ marginBottom: 0 }}>
                                <Select style={{ width: 140 }} options={[{ label: 'None', value: false }, { label: 'Underline', value: true }]} />
                              </Form.Item>
                            </Space>
                          </>
                        ),
                      },
                      {
                        key: 'border',
                        label: 'Border',
                        children: (
                          <Space wrap>
                            <Form.Item name={['defaultDeepNestedLineStyle', 'borderWidth']} label="Border width" style={{ marginBottom: 0 }}>
                              <InputNumber min={0} max={12} style={{ width: 140 }} />
                            </Form.Item>
                            <Form.Item name={['defaultDeepNestedLineStyle', 'borderStyle']} label="Border style" style={{ marginBottom: 0 }}>
                              <Select
                                style={{ width: 160 }}
                                options={[
                                  { label: 'None', value: 'none' },
                                  { label: 'Solid', value: 'solid' },
                                  { label: 'Dashed', value: 'dashed' },
                                  { label: 'Dotted', value: 'dotted' },
                                  { label: 'Double', value: 'double' },
                                ]}
                              />
                            </Form.Item>
                            <Form.Item label="Border color" style={{ marginBottom: 0 }}>
                              <Space wrap>
                                <Form.Item name={['defaultDeepNestedLineStyle', 'borderColor']} noStyle>
                                  <Input placeholder="#d9d9d9" style={{ width: 140 }} />
                                </Form.Item>
                                <ColorPicker
                                  value={(typeof bookDeepNestedLineBorderColor === 'string' && bookDeepNestedLineBorderColor.trim() ? bookDeepNestedLineBorderColor : undefined) || '#d9d9d9'}
                                  onChange={(c) => bookDefaultsForm.setFieldValue(['defaultDeepNestedLineStyle', 'borderColor'], c.toHexString())}
                                  showText
                                />
                              </Space>
                            </Form.Item>
                            <Form.Item name={['defaultDeepNestedLineStyle', 'borderRadius']} label="Border radius" style={{ marginBottom: 0 }}>
                              <InputNumber min={0} max={24} style={{ width: 160 }} />
                            </Form.Item>
                          </Space>
                        ),
                      },
                      {
                        key: 'spacing',
                        label: 'Spacing',
                        children: (
                          <Space wrap>
                            <Form.Item name={['defaultDeepNestedLineStyle', 'padding']} label="Padding" style={{ marginBottom: 0 }}>
                              <InputNumber min={0} max={64} style={{ width: 180 }} />
                            </Form.Item>
                            <Form.Item name={['defaultDeepNestedLineStyle', 'margin']} label="Margin" style={{ marginBottom: 0 }}>
                              <InputNumber min={0} max={64} style={{ width: 180 }} />
                            </Form.Item>
                          </Space>
                        ),
                      },
                      {
                        key: 'layout',
                        label: 'Layout',
                        children: (
                          <Form.Item name={['defaultDeepNestedLineStyle', 'align']} label="Align" initialValue="left">
                            <Select
                              options={[
                                { label: 'Left', value: 'left' },
                                { label: 'Center', value: 'center' },
                                { label: 'Right', value: 'right' },
                                { label: 'Justify', value: 'justify' },
                              ]}
                            />
                          </Form.Item>
                        ),
                      },
                    ]}
                  />
                ),
              },
                    ]}
                  />
                ),
              },
            ]}
            />

            <Form.Item noStyle shouldUpdate>
              {() => {
                const headerStyle = bookDefaultsForm.getFieldValue('defaultHeaderStyle');
                const lineStyle = bookDefaultsForm.getFieldValue('defaultLineStyle');
                const nestedLineStyle = bookDefaultsForm.getFieldValue('defaultNestedLineStyle');
                const deepNestedLineStyle = bookDefaultsForm.getFieldValue('defaultDeepNestedLineStyle');
                const linkStyle = bookDefaultsForm.getFieldValue('defaultLinkStyle');

                return (
                  <Card size="small" title="Live sample">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                          <Typography.Text type="secondary">Header</Typography.Text>
                          <Button size="small" type="text" icon={<EditOutlined />} onClick={() => openBookDefaultsSectionEditor('headerAdvanced')} />
                        </div>
                        <div style={{ marginTop: 4 }}>
                          <span style={asPreviewCss(headerStyle)}>Header / Section</span>
                        </div>
                      </div>

                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                          <Typography.Text type="secondary">Line + Link</Typography.Text>
                          <Button size="small" type="text" icon={<EditOutlined />} onClick={() => openBookDefaultsSectionEditor('lineAdvanced')} />
                        </div>
                        <div style={{ marginTop: 4 }}>
                          <span style={asPreviewCss(lineStyle)}>
                            This is a sample line with a <span style={asPreviewCss(linkStyle)}>link</span>.
                          </span>
                        </div>
                      </div>

                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                          <Typography.Text type="secondary">Nested line (level 1)</Typography.Text>
                          <Button size="small" type="text" icon={<EditOutlined />} onClick={() => openBookDefaultsSectionEditor('nestedLineAdvanced')} />
                        </div>
                        <div style={{ marginTop: 4 }}>
                          <span style={asPreviewCss(nestedLineStyle)}>Nested line (level 1)</span>
                        </div>
                      </div>

                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                          <Typography.Text type="secondary">Nested line (level 2+)</Typography.Text>
                          <Button size="small" type="text" icon={<EditOutlined />} onClick={() => openBookDefaultsSectionEditor('deepNestedLineAdvanced')} />
                        </div>
                        <div style={{ marginTop: 4, marginLeft: 20 }}>
                          <span style={asPreviewCss(deepNestedLineStyle)}>Nested line (level 2+)</span>
                        </div>
                      </div>
                    </div>
                  </Card>
                );
              }}
            </Form.Item>
          </div>
          */}

          <Typography.Paragraph type="secondary" style={{ marginTop: 0, marginBottom: 8 }}>
            Select an active template (applies to all Puja books). Per-book and per-block overrides still take priority.
          </Typography.Paragraph>
          <Space wrap style={{ width: '100%', justifyContent: 'space-between', marginBottom: 8 }}>
            <Space wrap>
              <div style={{ minWidth: 260 }}>
                <Typography.Text strong>Template</Typography.Text>
                <Select
                  value={selectedStyleTemplateId}
                  placeholder="Select template"
                  loading={styleTemplatesLoading}
                  style={{ width: 320 }}
                  options={(styleTemplates || []).map((t: any) => ({ label: t.name, value: t.id }))}
                  onChange={(v) => selectTemplate(v ?? null)}
                  allowClear
                />
              </div>
              <div>
                <Typography.Text strong>Active</Typography.Text>
                <Select
                  value={activeStyleTemplateId}
                  placeholder="No active template"
                  loading={styleTemplatesLoading}
                  style={{ width: 260 }}
                  options={[
                    ...(styleTemplates || []).map((t: any) => ({ label: t.name, value: t.id })),
                    { label: 'None', value: null as any },
                  ]}
                  onChange={async (v) => {
                    try {
                      await handleSetActive(v ?? null);
                    } catch {
                      message.error('Failed to set active template');
                    }
                  }}
                  allowClear
                />
              </div>
            </Space>
          </Space>

          <Divider style={{ margin: '12px 0' }} />
          <BookDefaultStylesEditor form={bookDefaultsForm} />
        </Form>
      </Modal>
    </div>
  );
};

export default PujaPage;
